import { useMemo, useState, type FormEvent } from 'react';
import {
  Box, Typography, Paper, Stack, Button, TextField, Chip, Alert, IconButton,
  Accordion, AccordionSummary, AccordionDetails, Dialog, DialogTitle, DialogContent,
  DialogActions, Tooltip,
} from '@mui/material';
import {
  DeleteOutlineOutlined, ExpandMoreOutlined, RestaurantOutlined, LightbulbOutlined,
  AddOutlined,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { Entry, MealIdea, MealTime } from '@lorrainemarwood-care/shared';
import { MEAL_TIMES } from '@lorrainemarwood-care/shared';
import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';

dayjs.extend(relativeTime);

// Starter ideas from care-plan.md §5 — kept inline so the page works offline
// and a fresh install has meaningful content. Merged with user-created ideas
// from the DB.
const STARTER_IDEAS: Record<MealTime, string[]> = {
  Breakfast: [
    'Porridge with stewed fruit',
    'Scrambled eggs',
    'Berry smoothie',
    'Weet-Bix with milk + banana',
    'Toast with butter and honey',
    'Yoghurt with a little muesli',
  ],
  'Morning tea': [
    'Cheese + crackers',
    'Apple slices with peanut butter',
    'Milo',
    'Plain biscuit + tea',
  ],
  Lunch: [
    'Pumpkin soup',
    'Chicken + leek soup',
    'Small ham sandwich',
    'Poached fish + mashed potato',
    'Scrambled egg on toast',
    'Cheese toastie, quartered',
  ],
  'Afternoon tea': [
    'Scone with jam + cream',
    'Lemon slice',
    'Fresh fruit',
    'Custard',
  ],
  Dinner: [
    'Slow-cooked beef stew',
    'Risotto',
    'Shepherd\'s pie',
    'Baked salmon + peas',
    'Soft meatballs in gravy',
    'Mashed potato + gravy',
  ],
  Snack: [
    'Yoghurt',
    'Ice cream',
    'Cheese + crackers',
    'Fresh fruit',
    'Protein smoothie (banana, yoghurt, honey, peanut butter)',
    'Jelly',
  ],
};

const PRINCIPLES = [
  'Small plates, 5–6 times a day, not 3 big meals',
  'Soft, moist foods — breathing + fatigue make chewing tiring',
  'Cold food if nausea is up',
  'High protein: smoothies, soups, eggs, cheese',
];

function pickDefaultMealType(): MealTime {
  const h = new Date().getHours();
  if (h < 10) return 'Breakfast';
  if (h < 12) return 'Morning tea';
  if (h < 14) return 'Lunch';
  if (h < 17) return 'Afternoon tea';
  if (h < 20) return 'Dinner';
  return 'Snack';
}

export default function Meals() {
  const { user } = useAuth();
  const [mealType, setMealType] = useState<MealTime>(pickDefaultMealType);
  const [text, setText] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const qc = useQueryClient();

  const entriesQuery = useQuery({
    queryKey: ['entries', { category: 'meal', limit: 100 }],
    queryFn: () => api.get<Entry[]>('/api/entries?category=meal&limit=100'),
  });

  const ideasQuery = useQuery({
    queryKey: ['meal-ideas'],
    queryFn: () => api.get<MealIdea[]>('/api/meal-ideas'),
  });

  const createEntryMutation = useMutation({
    mutationFn: () => api.post<Entry>('/api/entries', {
      category: 'meal',
      title: mealType,
      body: text.trim(),
    }),
    onSuccess: () => {
      setText('');
      qc.invalidateQueries({ queryKey: ['entries'] });
    },
  });

  const deleteEntryMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/entries/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['entries'] }),
  });

  const deleteIdeaMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/meal-ideas/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meal-ideas'] }),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    createEntryMutation.mutate();
  };

  const { todayEntries, earlierEntries } = useMemo(() => {
    const all = entriesQuery.data ?? [];
    const startOfToday = dayjs().startOf('day');
    const todayEntries = all.filter((e) => dayjs(e.occurredAt).isAfter(startOfToday));
    const earlierEntries = all.filter((e) => !dayjs(e.occurredAt).isAfter(startOfToday));
    return { todayEntries, earlierEntries };
  }, [entriesQuery.data]);

  // Ideas bucketed by meal time: starter (static strings) + user-added (DB rows)
  // merged, de-duped by text.
  const ideasByType = useMemo(() => {
    const userIdeas = ideasQuery.data ?? [];
    const out: Record<MealTime, Array<{ text: string; id?: string; createdByName?: string; createdById?: string }>> = {
      Breakfast: [], 'Morning tea': [], Lunch: [], 'Afternoon tea': [], Dinner: [], Snack: [],
    };
    for (const t of MEAL_TIMES) {
      const seen = new Set<string>();
      for (const starter of STARTER_IDEAS[t]) {
        seen.add(starter.toLowerCase());
        out[t].push({ text: starter });
      }
      for (const idea of userIdeas) {
        if (!idea.mealTimes.includes(t)) continue;
        const key = idea.text.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out[t].push({ text: idea.text, id: idea.id, createdByName: idea.createdByName, createdById: idea.createdById });
      }
    }
    return out;
  }, [ideasQuery.data]);

  const appendIdeaToForm = (ideaText: string) => {
    setText((prev) => prev ? `${prev}, ${ideaText}` : ideaText);
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 3 }}>
        <RestaurantOutlined />
        <Typography variant="h4" sx={{ fontWeight: 600 }}>Meals</Typography>
      </Stack>

      {/* Quick log */}
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Log a meal</Typography>
        <Box component="form" onSubmit={onSubmit}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
              {MEAL_TIMES.map((t) => (
                <Chip
                  key={t}
                  label={t}
                  onClick={() => setMealType(t)}
                  color={mealType === t ? 'primary' : 'default'}
                  variant={mealType === t ? 'filled' : 'outlined'}
                  clickable
                />
              ))}
            </Stack>
            <TextField
              label="What did she eat?"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="e.g. Half a bowl of pumpkin soup + a few crackers"
              multiline
              minRows={2}
              autoFocus
            />
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {ideasByType[mealType].map((idea) => (
                <Chip
                  key={idea.id ?? idea.text}
                  label={idea.text}
                  size="small"
                  variant="outlined"
                  onClick={() => appendIdeaToForm(idea.text)}
                  clickable
                />
              ))}
            </Stack>
            {createEntryMutation.isError && (
              <Alert severity="error">{(createEntryMutation.error as Error).message}</Alert>
            )}
            <Box>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={createEntryMutation.isPending || !text.trim()}
              >
                {createEntryMutation.isPending ? 'Saving…' : `Log ${mealType.toLowerCase()}`}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Paper>

      {/* Today */}
      <Typography variant="h6" sx={{ mb: 1.5 }}>Today</Typography>
      {todayEntries.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary', mb: 3 }}>
          Nothing logged yet today.
        </Box>
      ) : (
        <Stack spacing={1} sx={{ mb: 3 }}>
          {todayEntries.map((e) => (
            <MealRow
              key={e.id}
              entry={e}
              canDelete={user?.id === e.authorId || user?.role === 'admin'}
              onDelete={() => deleteEntryMutation.mutate(e.id)}
            />
          ))}
        </Stack>
      )}

      {/* Earlier */}
      {earlierEntries.length > 0 && (
        <Accordion defaultExpanded={false} disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, border: 1, borderColor: 'divider', borderRadius: 2, mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
            <Typography variant="subtitle1">Earlier · {earlierEntries.length}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {earlierEntries.slice(0, 30).map((e) => (
                <MealRow
                  key={e.id}
                  entry={e}
                  canDelete={user?.id === e.authorId || user?.role === 'admin'}
                  onDelete={() => deleteEntryMutation.mutate(e.id)}
                />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Principles */}
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
          <LightbulbOutlined fontSize="small" color="warning" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Guiding principles</Typography>
        </Stack>
        <Stack spacing={0.5} component="ul" sx={{ pl: 2.5, m: 0 }}>
          {PRINCIPLES.map((p) => (
            <Typography key={p} component="li" variant="body2" color="text.secondary">
              {p}
            </Typography>
          ))}
        </Stack>
      </Paper>

      {/* Ideas browser */}
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="h6">Meal ideas</Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddOutlined />}
          onClick={() => setAddOpen(true)}
        >
          Add idea
        </Button>
      </Stack>
      <Stack spacing={1}>
        {MEAL_TIMES.map((t) => (
          <Accordion
            key={t}
            defaultExpanded={false}
            disableGutters
            elevation={0}
            sx={{ '&:before': { display: 'none' }, border: 1, borderColor: 'divider', borderRadius: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t} <Typography component="span" variant="caption" color="text.secondary">· {ideasByType[t].length}</Typography>
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                {ideasByType[t].map((idea) => {
                  const canDelete = !!idea.id && (user?.id === idea.createdById || user?.role === 'admin');
                  return (
                    <Tooltip
                      key={idea.id ?? idea.text}
                      title={idea.createdByName ? `Added by ${idea.createdByName}` : 'Starter idea'}
                      arrow
                    >
                      <Chip
                        label={idea.text}
                        size="small"
                        variant="outlined"
                        color={idea.id ? 'primary' : 'default'}
                        onClick={() => {
                          setMealType(t);
                          appendIdeaToForm(idea.text);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        onDelete={canDelete ? () => deleteIdeaMutation.mutate(idea.id!) : undefined}
                        clickable
                      />
                    </Tooltip>
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>

      <AddIdeaDialog open={addOpen} onClose={() => setAddOpen(false)} />
    </Box>
  );
}

function AddIdeaDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [selected, setSelected] = useState<Set<MealTime>>(new Set());

  const createMutation = useMutation({
    mutationFn: () => api.post<MealIdea>('/api/meal-ideas', {
      text: text.trim(),
      mealTimes: Array.from(selected),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meal-ideas'] });
      setText('');
      setSelected(new Set());
      onClose();
    },
  });

  const toggle = (t: MealTime) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t); else next.add(t);
      return next;
    });
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim() || selected.size === 0) return;
    createMutation.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Add meal idea</DialogTitle>
      <Box component="form" onSubmit={onSubmit}>
        <DialogContent>
          <Stack spacing={2}>
            <TextField
              label="Idea"
              placeholder="e.g. Lamb & barley broth"
              value={text}
              onChange={(e) => setText(e.target.value)}
              required
              autoFocus
              size="small"
              fullWidth
            />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Suits which meal times? (pick one or more)
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                {MEAL_TIMES.map((t) => (
                  <Chip
                    key={t}
                    label={t}
                    onClick={() => toggle(t)}
                    color={selected.has(t) ? 'primary' : 'default'}
                    variant={selected.has(t) ? 'filled' : 'outlined'}
                    clickable
                  />
                ))}
              </Stack>
            </Box>
            {createMutation.isError && (
              <Alert severity="error">{(createMutation.error as Error).message}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={createMutation.isPending || !text.trim() || selected.size === 0}
          >
            {createMutation.isPending ? 'Saving…' : 'Add'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function MealRow({
  entry, canDelete, onDelete,
}: {
  entry: Entry;
  canDelete: boolean;
  onDelete: () => void;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            {entry.title && (
              <Chip size="small" label={entry.title} variant="outlined" color="primary" />
            )}
            <Typography variant="caption" color="text.secondary">
              {dayjs(entry.occurredAt).format('HH:mm')} · {entry.authorName} · {dayjs(entry.occurredAt).fromNow()}
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
            {entry.body}
          </Typography>
        </Box>
        {canDelete && (
          <IconButton size="small" onClick={onDelete}>
            <DeleteOutlineOutlined fontSize="small" />
          </IconButton>
        )}
      </Stack>
    </Paper>
  );
}
